import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from '@/shared/components';
import { formatReceivedAmount } from '@/shared/lib';
import { EXCHANGE_RATES_REFETCH_INTERVAL_MS } from '@/shared/config';
import { useSessionStore } from '@/entities/session';
import { useVerificationInfoModal } from '@/entities/verification';
import {
  EXCHANGE_RESULT_VARIANTS,
  EXCHANGE_SECTION_TYPES,
  type ExchangeAmountSource,
  type ExchangeResultVariant,
} from '../../model/types';
import { useExchangeAssets } from '../../model/useExchangeAssets';
import { useExchangeForm } from '../../model/useExchangeForm';
import { useExchangeConversion } from '../../model/useExchangeConversion';
import { AssetFromAvailableDescription } from '../AssetFromAvailableDescription';
import { ExchangeRotateButton } from '../ExchangeRotateButton';
import { ExchangeSectionField } from './ExchangeSectionField';
import { ExchangePercentChips } from './ExchangePercentChips';
import { ExchangeRateLine } from './ExchangeRateLine';
import { ExchangeSectionFooter } from './ExchangeSectionFooter';
import { ExchangeSectionSkeleton } from './ExchangeSectionSkeleton';

type Props = {
  variant?: ExchangeResultVariant;
  initialAssetFrom?: string;
  initialAssetTo?: string;
};

const ExchangeSection = ({
  variant = EXCHANGE_RESULT_VARIANTS.SECTION,
  initialAssetFrom,
  initialAssetTo,
}: Props) => {
  const { t } = useTranslation();
  const { session } = useSessionStore();
  const isUserVerified = session?.additional?.isVerified || false;
  const { openVerificationInfoModal } = useVerificationInfoModal();
  const [amountSource, setAmountSource] = useState<ExchangeAmountSource>(null);

  const {
    assetsExchangeFrom,
    assetsExchangeTo,
    selectedAssetToPay,
    setSelectedAssetToPay,
    selectedAssetToReceive,
    setSelectedAssetToReceive,
    isExchangeAssetsLoading,
  } = useExchangeAssets({ sortAssetsTo: false });

  const {
    form,
    assetCodeToPay,
    assetCodeToReceive,
    amountToPay,
    amountToReceive,
    payFieldErrorBeforeClick,
    receiveFieldErrorBeforeClick,
    onPayAmountBlur,
    onReceiveAmountBlur,
    formErrors,
    resetFormErrors,
    onSubmit,
    setExchangeRate,
    setAmountToPay,
    setAmountToReceive,
    setAssetCodeToPay,
    setAssetCodeToReceive,
    resetForm,
  } = useExchangeForm(assetsExchangeFrom, assetsExchangeTo, {
    requireBothAssets: true,
    confirmationTitle: t('exchange.sectionConfirmationTitle'),
    resultVariant: variant,
  });

  const {
    exchangeRateDescription,
    isConversionLoading,
    isRatesError,
    ratesErrorMessage,
    hasRates,
    recalculateAmountToReceive,
    recalculateAmountToPay,
  } = useExchangeConversion({
    amountToPay,
    amountToReceive,
    assetCodeToPay,
    assetCodeToReceive,
    amountSource,
    setExchangeRate,
    setAmountToPay,
    setAmountToReceive,
    ratesRefetchInterval: EXCHANGE_RATES_REFETCH_INTERVAL_MS,
  });

  useEffect(() => {
    const nextToPay = assetCodeToPay
      ? assetsExchangeFrom.find(asset => asset.code === assetCodeToPay)
      : undefined;
    const nextToReceive = assetCodeToReceive
      ? assetsExchangeTo.find(asset => asset.code === assetCodeToReceive)
      : undefined;

    if (nextToPay?.code !== selectedAssetToPay?.code) {
      setSelectedAssetToPay(nextToPay);
    }
    if (nextToReceive?.code !== selectedAssetToReceive?.code) {
      setSelectedAssetToReceive(nextToReceive);
    }
  }, [
    assetCodeToPay,
    assetCodeToReceive,
    assetsExchangeFrom,
    assetsExchangeTo,
    selectedAssetToPay?.code,
    selectedAssetToReceive?.code,
    setSelectedAssetToPay,
    setSelectedAssetToReceive,
  ]);

  const isInitialAssetAppliedRef = useRef(false);

  useEffect(() => {
    if (
      isInitialAssetAppliedRef.current ||
      (!initialAssetFrom && !initialAssetTo) ||
      isExchangeAssetsLoading ||
      assetsExchangeFrom.length === 0
    ) {
      return;
    }

    isInitialAssetAppliedRef.current = true;

    if (assetCodeToPay || assetCodeToReceive) {
      return;
    }

    if (
      initialAssetFrom &&
      assetsExchangeFrom.some(asset => asset.code === initialAssetFrom)
    ) {
      setAssetCodeToPay(initialAssetFrom);
    }

    if (
      initialAssetTo &&
      assetsExchangeTo.some(asset => asset.code === initialAssetTo)
    ) {
      setAssetCodeToReceive(initialAssetTo);
    }
  }, [
    initialAssetFrom,
    initialAssetTo,
    isExchangeAssetsLoading,
    assetsExchangeFrom,
    assetsExchangeTo,
    assetCodeToPay,
    assetCodeToReceive,
    setAssetCodeToPay,
    setAssetCodeToReceive,
  ]);

  const setAssetFromAmount = useCallback(
    (amount: string) => {
      setAmountSource(amount ? 'pay' : null);
      setAmountToPay(amount);
      recalculateAmountToReceive(amount);
    },
    [setAmountToPay, recalculateAmountToReceive]
  );

  const setAssetToAmount = useCallback(
    (amount: string) => {
      setAmountSource(amount ? 'receive' : null);
      setAmountToReceive(amount);
      recalculateAmountToPay(amount);
    },
    [setAmountToReceive, recalculateAmountToPay]
  );

  const handleAssetFromChange = useCallback(
    (code: string) => {
      if (!assetCodeToPay || code === assetCodeToPay) return;
      setAmountToPay('');
      if (amountSource !== 'receive') {
        setAmountToReceive('');
        setAmountSource(null);
      }
    },
    [amountSource, assetCodeToPay, setAmountToPay, setAmountToReceive]
  );

  const handleAssetToChange = useCallback(
    (code: string) => {
      if (!assetCodeToReceive || code === assetCodeToReceive) return;
      setAmountToReceive('');
      if (amountSource !== 'pay') {
        setAmountToPay('');
        setAmountSource(null);
      }
    },
    [amountSource, assetCodeToReceive, setAmountToPay, setAmountToReceive]
  );

  const handleQuickSelectFrom = useCallback(
    (code: string) => {
      handleAssetFromChange(code);
      setAssetCodeToPay(code);
    },
    [setAssetCodeToPay, handleAssetFromChange]
  );

  const handleQuickSelectTo = useCallback(
    (code: string) => {
      handleAssetToChange(code);
      setAssetCodeToReceive(code);
    },
    [setAssetCodeToReceive, handleAssetToChange]
  );

  const rotateAssets = useCallback(() => {
    if (!assetCodeToPay || !assetCodeToReceive) return;

    resetForm({
      assetFrom: assetCodeToReceive,
      assetTo: assetCodeToPay,
      amount: '',
    });
    setAmountToReceive('');
    setAmountSource(null);
    resetFormErrors();
  }, [
    assetCodeToPay,
    assetCodeToReceive,
    resetForm,
    setAmountToReceive,
    resetFormErrors,
  ]);

  const handleSubmit = () => {
    if (!isUserVerified) {
      openVerificationInfoModal();
      return;
    }
    onSubmit(form.getValues());
  };

  const available = selectedAssetToPay?.available ?? 0;
  const isSubmitDisabled =
    !(Number(amountToPay) > 0) ||
    !selectedAssetToPay ||
    !selectedAssetToReceive ||
    Number(amountToPay) > available ||
    !hasRates ||
    isRatesError ||
    isConversionLoading;

  const total =
    Number(amountToReceive) > 0 && selectedAssetToReceive
      ? `${formatReceivedAmount(Number(amountToReceive))} ${selectedAssetToReceive.code}`
      : formatReceivedAmount(0);

  if (isExchangeAssetsLoading) {
    return <ExchangeSectionSkeleton />;
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-[2.4rem]">
        <div className="flex flex-col gap-[2.4rem] tablet:gap-[3.6rem]">
          <ExchangeSectionField
            type={EXCHANGE_SECTION_TYPES.FROM}
            title={t('exchange.sectionSell')}
            headerContent={
              selectedAssetToPay &&
              available > 0 && (
                <ExchangePercentChips
                  assetToPay={selectedAssetToPay}
                  onSelect={amount => {
                    resetFormErrors();
                    setAssetFromAmount(amount);
                  }}
                />
              )
            }
            description={
              selectedAssetToPay && (
                <span className="medium5 text-secondary">
                  <AssetFromAvailableDescription
                    code={selectedAssetToPay.code}
                    available={available}
                    onClick={() => setAssetFromAmount(String(available))}
                  />
                </span>
              )
            }
            error={
              payFieldErrorBeforeClick?.message ||
              formErrors.payFieldErrorAfterClick
            }
            isAmountError={
              payFieldErrorBeforeClick?.target === 'amount' ||
              !!formErrors.payFieldErrorAfterClick
            }
            isSelectError={
              payFieldErrorBeforeClick?.target === 'select' ||
              !!formErrors.payFieldErrorAfterClick
            }
            amount={amountToPay}
            setAmount={setAssetFromAmount}
            onAmountBlur={onPayAmountBlur}
            assetsOptions={assetsExchangeFrom}
            selectedAsset={selectedAssetToPay}
            onAssetChange={handleAssetFromChange}
            onQuickSelect={handleQuickSelectFrom}
            resetFormErrors={resetFormErrors}
          />

          <ExchangeSectionField
            type={EXCHANGE_SECTION_TYPES.TO}
            title={t('exchange.sectionBuy')}
            titleContent={
              selectedAssetToPay &&
              selectedAssetToReceive && (
                <ExchangeRotateButton
                  onClick={rotateAssets}
                  disabled={isConversionLoading}
                />
              )
            }
            description={
              selectedAssetToPay &&
              selectedAssetToReceive && (
                <ExchangeRateLine
                  description={exchangeRateDescription}
                  isLoading={isConversionLoading}
                  errorMessage={isRatesError ? ratesErrorMessage : null}
                />
              )
            }
            error={
              receiveFieldErrorBeforeClick?.message ||
              formErrors.receiveFieldErrorAfterClick
            }
            isAmountError={!!formErrors.receiveFieldErrorAfterClick}
            isSelectError={
              !!receiveFieldErrorBeforeClick ||
              !!formErrors.receiveFieldErrorAfterClick
            }
            amount={amountToReceive}
            setAmount={setAssetToAmount}
            onAmountBlur={onReceiveAmountBlur}
            assetsOptions={assetsExchangeTo}
            selectedAsset={selectedAssetToReceive}
            onAssetChange={handleAssetToChange}
            onQuickSelect={handleQuickSelectTo}
            resetFormErrors={resetFormErrors}
          />
        </div>

        <ExchangeSectionFooter
          total={total}
          isSubmitDisabled={isSubmitDisabled}
          onSubmit={handleSubmit}
        />
      </form>
    </Form>
  );
};

export { ExchangeSection };
